import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';

// Utils
import { getStringifiedQueryParams } from 'utils/helpers/fetch';
import http from 'utils/http';

const initialState = {
  areAllCustomersAllowed: 'true',
  authorizationBy: 'carrier',
  customersList: [],
  detailSearchTerm: '',
  forceClose: false,
  hasChangesToStepOne: false,
  hasChangesToStepTwo: false,
  hasUnsavedChanges: false,
  isCustomersListPending: false,
  params: {
    viewById: undefined,
  },
  selectCustomersSearchTerm: '',
  selectStepSearchTerm: '',
  selectedCustomers: [],
  submittingBatch: false,
  supplier: {},
  unauthorizedList: [],
  unauthorizedListPending: false,
  viewByList: [],
};

const reducers = {
  resetCarrierAuthWizardData: payload =>
    R.assoc('authorizationBy', R.defaultTo('carrier', payload), initialState),
  setAreAllCustomersAllowed: R.assoc('areAllCustomersAllowed'),
  setAuthorizationBy: (value, state) =>
    R.pipe(R.assoc('authorizationBy', value), R.assoc('hasChangesToStepOne', true))(state),
  setBatchEnabledOptions: (updates, state) => {
    const idKey = `${state.authorizationBy === 'carrier' ? 'location' : 'carrier'}Id`;
    const updateMap = new Map(updates.map(({ id, selected }) => [parseInt(id), selected]));
    return R.assoc(
      'unauthorizedList',
      state.unauthorizedList.map(item =>
        updateMap.has(item[idKey]) ? { ...item, selected: updateMap.get(item[idKey]) } : item
      ),
      state
    );
  },
  setCustomersList: R.assoc('customersList'),
  setDetailSearchTerm: R.assoc('detailSearchTerm'),
  setEnabledOptions: ({ id, selected }, state) => {
    const index = R.findIndex(
      R.propEq(`${state.authorizationBy === 'carrier' ? 'location' : 'carrier'}Id`, parseInt(id)),
      state.unauthorizedList
    );
    return R.assocPath(['unauthorizedList', index, 'selected'], selected, state);
  },
  setForceClose: R.assoc('forceClose'),
  setHasChangesToStepOne: R.assoc('hasChangesToStepOne'),
  setHasChangesToStepTwo: R.assoc('hasChangesToStepTwo'),
  setHasUnsavedChanges: R.assoc('hasUnsavedChanges'),
  setIsCustomersListPending: R.assoc('isCustomersListPending'),
  setParams: (params, state) =>
    R.pipe(
      R.assoc('params', R.mergeDeepLeft(params, state.params)),
      R.assoc('hasChangesToStepOne', true)
    )(state),
  setSelectCustomersSearchTerm: R.assoc('selectCustomersSearchTerm'),
  setSelectStepSearchTerm: R.assoc('selectStepSearchTerm'),
  setSelectedCustomers: R.assoc('selectedCustomers'),
  setSubmittingBatch: R.assoc('submittingBatch'),
  setUnauthorizedList: R.assoc('unauthorizedList'),
  setUnauthorizedListPending: R.assoc('unauthorizedListPending'),
  setViewByList: R.assoc('viewByList'),
  setWizardDetails: ({ name, value }, state) =>
    R.pipe(R.assoc(name, value), R.assoc('hasChangesToStepOne', true))(state),
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'createNewCarrierAuth',
  // eslint-disable-next-line react-hooks/rules-of-hooks
  reducers: R.map(singleReducer => R.flip(R.useWith(singleReducer, [R.prop('payload')])), reducers),
});

export const {
  resetCarrierAuthWizardData,
  setAreAllCustomersAllowed,
  setAuthorizationBy,
  setCustomersList,
  setDetailSearchTerm,
  setEnabledOptions,
  setForceClose,
  setBatchEnabledOptions,
  setHasChangesToStepOne,
  setHasChangesToStepTwo,
  setHasUnsavedChanges,
  setIsCustomersListPending,
  setSelectedCustomers,
  setSelectCustomersSearchTerm,
  setSelectStepSearchTerm,
  setSubmittingBatch,
  setUnauthorizedList,
  setUnauthorizedListPending,
  setViewByList,
  setParams,
  setUnauthorizedListError,
  setWizardDetails,
} = actions;

// Selectors
const mergePath = R.concat(['loadingManagement', 'createNewCarrierAuth']);
export const getStoreValueByProp = R.o(R.path, mergePath);

export const getViewBySelection = createSelector(
  getStoreValueByProp(['authorizationBy']),
  getStoreValueByProp(['params', 'viewById']),
  getStoreValueByProp(['viewByList']),
  (authBy, id, items) => R.find(R.propEq(`${authBy}Id`, id), items)
);

export const getViewByListOptions = createSelector(
  getStoreValueByProp(['viewByList']),
  getStoreValueByProp(['authorizationBy']),
  (items, authBy) => {
    const carrierFEIN = authBy === 'carrier' ? 'carrierFEIN' : null;
    const addDashes = str => str.replace(/^(.{2})/, '$1-');
    const id = `${authBy}Id`;
    const name = authBy === 'carrier' ? 'carrierName' : 'nameShort';
    return R.map(
      item => ({
        label: item[carrierFEIN] ? `${addDashes(item[carrierFEIN])} ${item[name]}` : item[name],
        value: item[id],
      }),
      items
    );
  }
);

export const getInactiveListOptions = createSelector(
  getStoreValueByProp(['unauthorizedList']),
  getStoreValueByProp(['authorizationBy']),
  (items, authBy) => {
    const authByName = authBy === 'carrier' ? 'location' : 'carrier';
    const label = authBy === 'carrier' ? 'nameLong' : 'carrierName';
    const id = `${authByName}Id`;
    return R.map(
      item => ({
        fein: item.carrierFein,
        label: item[label],
        scac: item.carrierScac,
        selected: !!item.selected,
        value: item[id],
      }),
      items
    );
  }
);

export const getAllEnabled = createSelector(
  getStoreValueByProp(['unauthorizedList']),
  R.all(R.prop('selected'))
);

export const getStepTwoDisableContinue = createSelector(
  getStoreValueByProp(['unauthorizedList']),
  R.none(R.prop('selected'))
);

// Step 3: customer restriction
export const getStepThreeDisableContinue = createSelector(
  getStoreValueByProp(['selectedCustomers']),
  getStoreValueByProp(['areAllCustomersAllowed']),
  (selectedCustomers, areAllAllowed) => areAllAllowed === 'false' && R.isEmpty(selectedCustomers)
);

// Summary Step
export const getEnabledOptions = createSelector(
  getStoreValueByProp(['authorizationBy']),
  getStoreValueByProp(['unauthorizedList']),
  (authBy, items) => {
    const id = authBy === 'carrier' ? 'locationId' : 'carrierId';
    const name = authBy === 'carrier' ? 'nameShort' : 'carrierName';
    return items.flatMap(item =>
      item.selected
        ? [
            {
              label: item[name],
              value: item[id],
            },
          ]
        : []
    );
  }
);

// Actions
export const fetchViewByList = () => async (dispatch, getState) => {
  const {
    authorizationBy,
    supplier: { customerIdName: companyCustomerIdName, dataCompanyId },
  } = getState().loadingManagement.createNewCarrierAuth;

  const queryString = getStringifiedQueryParams({
    supplier: { companyCustomerIdName, dataCompanyId },
  });

  const URL = `carrier-authorizations/get-${authorizationBy}s?${queryString}`;
  http.get(URL).then(({ data }) => {
    dispatch(setViewByList(data));
  });
};

export const fetchCustomers = () => async (dispatch, getState) => {
  dispatch(setIsCustomersListPending(true));

  const {
    authorizationBy,
    hasChangesToStepTwo,
    params,
    supplier: { customerIdName: companyCustomerIdName, dataCompanyId },
    unauthorizedList,
  } = getState().loadingManagement.createNewCarrierAuth;

  const selectedLocations =
    authorizationBy === 'location'
      ? [params.viewById]
      : R.pluck(
          'locationId',
          unauthorizedList.filter(location => R.equals(location.selected, true))
        );

  if (hasChangesToStepTwo) {
    dispatch(setAreAllCustomersAllowed('true'));
    dispatch(setCustomersList([]));
    dispatch(setHasChangesToStepTwo(false));
  }

  const queryString = getStringifiedQueryParams({
    locationIds: selectedLocations,
    supplier: { companyCustomerIdName, dataCompanyId },
  });

  const URL = `carrier-authorizations/get-customers?${queryString}`;

  http
    .get(URL)
    .then(({ data }) => {
      dispatch(setCustomersList(data));
    })
    .finally(() => dispatch(setIsCustomersListPending(false)));
};

export const fetchUnauthorizedList = () => async (dispatch, getState) => {
  dispatch(setUnauthorizedListPending(true));

  const {
    authorizationBy,
    hasChangesToStepOne,
    supplier: { customerIdName: companyCustomerIdName, dataCompanyId },
    params: { viewById },
    unauthorizedList: existingList,
  } = getState().loadingManagement.createNewCarrierAuth;

  const paramKey = `${authorizationBy}Id`;
  const endpointName = authorizationBy === 'location' ? 'carrier' : 'location';

  const queryString = getStringifiedQueryParams({
    [paramKey]: viewById,
    supplier: { companyCustomerIdName, dataCompanyId },
  });
  const URL = `carrier-authorizations/get-${authorizationBy}-unauthorized-${endpointName}s?${queryString}`;
  http
    .get(URL)
    .then(({ data }) => {
      let dataToStore = data;
      // Only preserve existing selections if Step 1 hasn't changed
      if (!hasChangesToStepOne) {
        const keyToMatch = authorizationBy === 'carrier' ? 'locationId' : 'carrierId';
        const selectedIds = new Set(
          existingList.filter(item => item.selected).map(item => item[keyToMatch])
        );
        dataToStore = data.map(item => ({
          ...item,
          selected: selectedIds.has(item[keyToMatch]) || false,
        }));
      }
      dispatch(setUnauthorizedList(dataToStore));
      dispatch(setHasChangesToStepOne(false));
    })
    .finally(() => dispatch(setUnauthorizedListPending(false)));
};

export const createNewCarrierAuth = enabledOption => async (_, getState) => {
  const {
    params: { viewById },
    supplier: { dataCompanyId, customerIdName: companyCustomerIdName },
    areAllCustomersAllowed,
    authorizationBy,
    selectedCustomers,
  } = getState().loadingManagement.createNewCarrierAuth;

  const id = authorizationBy === 'carrier' ? 'locationId' : 'carrierId';

  const payload = {
    [`${authorizationBy}Id`]: viewById,
    ...(R.equals(areAllCustomersAllowed, 'false') && {
      customerIds: R.pluck('customerId', selectedCustomers),
    }),
    supplier: { companyCustomerIdName, dataCompanyId },
    ...enabledOption,
  };
  return http
    .post('carrier-authorizations/create-authorization', payload)
    .then(
      R.always({
        error: '',
        id: enabledOption[id],
        success: true,
      })
    )
    .catch(error => {
      if (error.response?.data) {
        return {
          error: error.response.data,
          id: enabledOption[id],
          success: false,
        };
      }
      if (error.request?.statusText) {
        return {
          error: error.request.statusText,
          id: enabledOption[id],
          success: false,
        };
      }
      return {
        error: error.message || 'Unknown Error',
        id: enabledOption[id],
        success: false,
      };
    });
};

export default reducer;
